import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
    name: 'sanitizeHtml',
    standalone: false
})
export class SanitizeHtmlPipe implements PipeTransform {
  private static nextSvgScopeId = 0;

  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return '';
    }

    const normalized = this.normalizeSvgMarkup(value);
    const scoped = this.scopeSvgIds(normalized);
    return this.sanitizer.bypassSecurityTrustHtml(scoped);
  }

  private normalizeSvgMarkup(value: string): string {
    const trimmed = value.trimStart();
    if (!trimmed.startsWith('<svg')) {
      return value;
    }

    return value.replace(/<svg\b([^>]*)>/i, (_match, attrs: string) => {
      const withoutWidth = attrs.replace(/\swidth\s*=\s*(["']).*?\1/gi, '');
      const withoutHeight = withoutWidth.replace(/\sheight\s*=\s*(["']).*?\1/gi, '');
      const hasPreserveAspectRatio = /\spreserveAspectRatio\s*=\s*(["']).*?\1/i.test(withoutHeight);
      const styleMatch = withoutHeight.match(/\sstyle\s*=\s*(["'])(.*?)\1/i);

      const responsiveStyle = 'display:block;width:100%;height:100%;max-width:100%;max-height:100%;';
      let mergedAttributes = withoutHeight;

      if (styleMatch) {
        const quote = styleMatch[1];
        const existingStyle = styleMatch[2].trim();
        const delimiter = existingStyle.length > 0 && !existingStyle.endsWith(';') ? ';' : '';
        const mergedStyle = `${existingStyle}${delimiter}${responsiveStyle}`;
        mergedAttributes = withoutHeight.replace(styleMatch[0], ` style=${quote}${mergedStyle}${quote}`);
      } else {
        mergedAttributes = `${withoutHeight} style="${responsiveStyle}"`;
      }

      if (!hasPreserveAspectRatio) {
        mergedAttributes = `${mergedAttributes} preserveAspectRatio="xMidYMid meet"`;
      }

      return `<svg${mergedAttributes} width="100%" height="100%">`;
    });
  }

  private scopeSvgIds(value: string): string {
    const trimmed = value.trimStart();
    if (!trimmed.startsWith('<svg')) {
      return value;
    }

    const ids = [...value.matchAll(/\sid\s*=\s*(["'])([^"']+)\1/gi)]
      .map(match => match[2])
      .filter((id, index, all) => all.indexOf(id) === index);

    if (ids.length === 0) {
      return value;
    }

    const scopePrefix = `svg-scope-${SanitizeHtmlPipe.nextSvgScopeId++}`;
    return ids.reduce(
      (svg, id) => this.replaceSvgReference(svg, id, `${scopePrefix}-${id}`),
      value
    );
  }

  private replaceSvgReference(value: string, originalId: string, scopedId: string): string {
    const escapedId = this.escapeRegExp(originalId);

    return value
      .replace(new RegExp(`(\\sid\\s*=\\s*["'])${escapedId}(["'])`, 'g'), `$1${scopedId}$2`)
      .replace(new RegExp(`url\\(#${escapedId}\\)`, 'g'), `url(#${scopedId})`)
      .replace(new RegExp(`((?:href|xlink:href)\\s*=\\s*["'])#${escapedId}(["'])`, 'g'), `$1#${scopedId}$2`);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
